import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check, ArrowLeft, Edit, Send, Trash2, Plus } from 'lucide-react';
import { useTitle } from '../context/TitleContext';
import { getTemplatesByCategory, StarterTemplate } from '../data/starterTemplates';
import { DocumentTemplate, generateId, loadTemplates, saveTemplates } from '../models/templateModels';
import { useAuth } from '../context/AuthContext';

type ViewMode = 'main' | 'category' | 'template' | 'preview';

const NewDocumentRequest = () => {
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('main');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<StarterTemplate | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<DocumentTemplate[]>([]);

  const templatesByCategory = getTemplatesByCategory();
  const categories = Object.keys(templatesByCategory);

  // Set page title
  useEffect(() => {
    setTitle(t('newRequestTemplate'));
    document.title = t('newRequestTemplate');
  }, [setTitle, t]);

  // Load saved templates
  useEffect(() => {
    const templates = loadTemplates();
    setSavedTemplates(templates);
  }, []);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setViewMode('template');
  };

  const handleTemplateSelect = (template: StarterTemplate) => {
    setSelectedTemplate(template);
    setViewMode('preview');
  };

  const handleCreateFromTemplate = () => {
    if (!selectedTemplate) return;

    // Create new template from starter
    const now = new Date().toISOString();
    const newTemplate: DocumentTemplate = {
      ...selectedTemplate.template,
      id: generateId(),
      createdAt: now,
      updatedAt: now,
      createdBy: currentUser?.id || 'unknown'
    };

    // Save to localStorage
    const templates = loadTemplates();
    templates.push(newTemplate);
    saveTemplates(templates);

    // Navigate to edit page so user can customize
    navigate(`/templates/${newTemplate.id}/edit`);
  };

  const handleStartFromScratch = () => {
    navigate('/templates/new');
  };

  const handleEditSavedTemplate = (templateId: string) => {
    navigate(`/templates/${templateId}/edit`);
  };

  const handleSendSavedTemplate = (templateId: string) => {
    navigate(`/templates/${templateId}/send`);
  };

  const handleDeleteSavedTemplate = (templateId: string) => {
    if (confirm(t('confirmDeleteTemplate'))) {
      const updatedTemplates = savedTemplates.filter(t => t.id !== templateId);
      saveTemplates(updatedTemplates);
      setSavedTemplates(updatedTemplates);
    }
  };

  const handleBack = () => {
    if (viewMode === 'preview') {
      setViewMode('template');
      setSelectedTemplate(null);
    } else if (viewMode === 'template') {
      setViewMode('category');
      setSelectedCategory('');
    } else if (viewMode === 'category') {
      setViewMode('main');
    }
  };

  // Main view - shows saved templates and starter template categories
  if (viewMode === 'main') {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Saved Custom Templates Section - Always Show */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t('myTemplates')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              {t('yourSavedTemplates')}
            </p>
          </div>
          
          {savedTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedTemplates.map((template) => {
                const requiredCount = template.items.filter(item => item.isRequired).length;
                const optionalCount = template.items.filter(item => !item.isRequired).length;
                return (
                  <div
                    key={template.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow"
                  >
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate">
                        {template.name}
                      </h3>
                      {template.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400 mb-4">
                      <span className="flex items-center">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">{requiredCount}</span>
                        <span className="ml-1">{t('required')}</span>
                      </span>
                      <span>•</span>
                      <span className="flex items-center">
                        <span className="font-medium">{optionalCount}</span>
                        <span className="ml-1">{t('optional')}</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSendSavedTemplate(template.id)}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        {t('send')}
                      </button>
                      <button
                        onClick={() => handleEditSavedTemplate(template.id)}
                        className="inline-flex items-center justify-center p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSavedTemplate(template.id)}
                        className="inline-flex items-center justify-center p-2 bg-gray-100 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 text-center border-2 border-dashed border-gray-300 dark:border-gray-600">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('noSavedTemplates')}
              </p>
              <button
                onClick={handleStartFromScratch}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('createFirstTemplate')}
              </button>
            </div>
          )}
        </div>

        {/* Starter Templates Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t('starterTemplates')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              {t('selectCategoryDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
            {categories.map((category) => {
              const templates = templatesByCategory[category];
              return (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-5 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {t(category.toLowerCase())}
                    </h3>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {templates.length} {t('templatesAvailable')}
                  </p>
                </button>
              );
            })}
          </div>

          <button
            onClick={handleStartFromScratch}
            className="w-full bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-5 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all"
          >
            <div className="flex items-center justify-center">
              <Plus className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-base font-medium text-gray-700 dark:text-gray-300">
                {t('startFromScratch')}
              </span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Category selection view
  if (viewMode === 'category') {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('selectCategory')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
            {t('selectCategoryDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {categories.map((category) => {
            const templates = templatesByCategory[category];
            return (
              <button
                key={category}
                onClick={() => handleCategorySelect(category)}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    {t(category.toLowerCase())}
                  </h3>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  {templates.length} {t('templatesAvailable')}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Template selection view
  if (viewMode === 'template' && selectedCategory) {
    const templates = templatesByCategory[selectedCategory];
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t(selectedCategory.toLowerCase())}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
            {t('chooseTemplateToPreview')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all text-left group"
            >
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="text-3xl sm:text-4xl flex-shrink-0">{template.icon}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 mb-2 truncate">
                    {template.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {template.description}
                  </p>
                  <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {template.template.items.filter(item => item.isRequired).length} {t('required')}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {template.template.items.filter(item => !item.isRequired).length} {t('optional')}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Template preview view
  if (viewMode === 'preview' && selectedTemplate) {
    const requiredDocs = selectedTemplate.template.items.filter(item => item.isRequired);
    const optionalDocs = selectedTemplate.template.items.filter(item => !item.isRequired);

    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('templatePreview')}
          </h1>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3 sm:space-x-4">
            <div className="text-4xl sm:text-5xl flex-shrink-0">{selectedTemplate.icon}</div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {selectedTemplate.template.name}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-3">
                {selectedTemplate.template.description}
              </p>
              <span className="inline-block px-2 sm:px-3 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full text-xs sm:text-sm font-medium">
                {t(selectedCategory.toLowerCase())}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 border border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('includedDocuments')}
          </h3>

          {requiredDocs.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                {t('requiredDocuments')}
              </h4>
              <div className="space-y-3">
                {requiredDocs.map((item, index) => (
                  <div key={item.order} className="flex items-start space-x-2 sm:space-x-3">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white break-words">
                        {index + 1}. {t(item.nameKey || item.customName || '')}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                        {t(item.descriptionKey || item.customDescription || '')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {optionalDocs.length > 0 && (
            <div>
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                {t('optionalDocuments')}
              </h4>
              <div className="space-y-2">
                {optionalDocs.map((item, index) => (
                  <div key={item.order} className="text-gray-700 dark:text-gray-300 min-w-0">
                    <p className="text-sm sm:text-base font-medium break-words">
                      {index + 1}. {t(item.nameKey || item.customName || '')}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                      {t(item.descriptionKey || item.customDescription || '')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 sm:space-x-4">
          <button
            onClick={handleBack}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleCreateFromTemplate}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
          >
            {t('useThisTemplate')}
          </button>
        </div>
      </div>
    );
  }

  return null;
};
  
export default NewDocumentRequest;